import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  autoFitView,
  buildTicks,
  DOUBLE_TAP_MS,
  isZoomedView,
  niceStep,
  panViewByPlotDrag,
  POINTER_THRESHOLD_PX,
  viewSpan,
  YAxisView,
  zoomViewByAxisDrag,
} from 'common/yZoom';

export type YAxisId = 'left' | 'right';

type GestureRegion = YAxisId | 'plot';

type ActiveGesture = {
  pointerId: number;
  region: GestureRegion;
  startClientX: number;
  startClientY: number;
  /** Plot-relative Y at gesture start (0 = top of cartesian grid). */
  anchorPlotY: number;
  plotHeight: number;
  /** Snapshot of full axis views at gesture start. */
  startLeft: YAxisView;
  startRight: YAxisView;
  moved: boolean;
};

export type UseYAxisZoomArgs = {
  /** Data-derived auto-fit ceiling for the primary / left axis. */
  leftDataMax: number;
  /** Secondary / right axis ceiling when dual axes are active; otherwise null. */
  rightDataMax: number | null;
  /**
   * Changes whenever timeframe, insights, jackpot overlay, or machine changes —
   * all axis zoom state resets.
   */
  resetKey: string | number;
};

const emptyView = (): YAxisView => ({ min: 0, max: 1 });

/**
 * Pointer-driven Y zoom (axis strips) and vertical pan (plot, only while zoomed).
 * View is a sliding window [min, max] with min ≥ 0; math lives in common/yZoom.ts.
 */
export const useYAxisZoom = ({ leftDataMax, rightDataMax, resetKey }: UseYAxisZoomArgs) => {
  const leftAutoFitMax = useMemo(() => autoFitView(leftDataMax).max, [leftDataMax]);
  const rightAutoFitMax = useMemo(
    () => (rightDataMax == null ? null : autoFitView(rightDataMax).max),
    [rightDataMax]
  );

  const [leftView, setLeftView] = useState<YAxisView>(() => autoFitView(leftDataMax));
  const [rightView, setRightView] = useState<YAxisView>(emptyView);

  const chartAreaRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<ActiveGesture | null>(null);
  const lastTapRef = useRef<{ axis: YAxisId; at: number } | null>(null);
  // Keep latest views in refs so pointer handlers don't re-bind every frame.
  const leftViewRef = useRef(leftView);
  const rightViewRef = useRef(rightView);
  const leftAutoRef = useRef(leftAutoFitMax);
  const rightAutoRef = useRef(rightAutoFitMax);
  leftViewRef.current = leftView;
  rightViewRef.current = rightView;
  leftAutoRef.current = leftAutoFitMax;
  rightAutoRef.current = rightAutoFitMax;

  const resetAll = useCallback(() => {
    setLeftView(autoFitView(leftDataMax));
    setRightView(rightDataMax == null ? emptyView() : autoFitView(rightDataMax));
  }, [leftDataMax, rightDataMax]);

  useEffect(() => {
    resetAll();
  }, [resetKey, resetAll]);

  const resetAxis = useCallback((axis: YAxisId) => {
    if (axis === 'left') {
      setLeftView({ min: 0, max: leftAutoRef.current });
    } else if (rightAutoRef.current != null) {
      setRightView({ min: 0, max: rightAutoRef.current });
    }
  }, []);

  const leftZoomed = isZoomedView(leftView, leftAutoFitMax);
  const rightZoomed =
    rightAutoFitMax != null && isZoomedView(rightView, rightAutoFitMax);
  const isAnyZoomed = leftZoomed || rightZoomed;

  const leftStep = useMemo(() => niceStep(viewSpan(leftView), 4), [leftView]);
  const rightStep = useMemo(
    () => (rightAutoFitMax == null ? 1 : niceStep(viewSpan(rightView), 4)),
    [rightAutoFitMax, rightView]
  );
  const leftTicks = useMemo(
    () => buildTicks(leftView.min, leftView.max, leftStep),
    [leftView.min, leftView.max, leftStep]
  );
  const rightTicks = useMemo(
    () =>
      rightAutoFitMax == null ? [] : buildTicks(rightView.min, rightView.max, rightStep),
    [rightAutoFitMax, rightView.min, rightView.max, rightStep]
  );

  const measurePlot = useCallback(() => {
    const root = chartAreaRef.current;
    if (!root) return null;
    const gridEl =
      root.querySelector('.recharts-cartesian-grid') ?? root.querySelector('.recharts-surface');
    if (!(gridEl instanceof HTMLElement) && !(gridEl instanceof SVGElement)) return null;
    const plotRect = gridEl.getBoundingClientRect();
    const yAxes = Array.from(root.querySelectorAll('.recharts-yAxis')).filter(
      (el): el is HTMLElement | SVGElement =>
        el instanceof HTMLElement || el instanceof SVGElement
    );
    const leftAxisEl = yAxes.find((el) => {
      const rect = el.getBoundingClientRect();
      return rect.left <= plotRect.left + 1;
    }) ?? yAxes[0];
    const rightAxisEl =
      yAxes.find((el) => {
        const rect = el.getBoundingClientRect();
        return rect.left >= plotRect.right - 1;
      }) ?? (yAxes.length > 1 ? yAxes[yAxes.length - 1] : null);
    return {
      plotRect,
      leftAxisRect: leftAxisEl?.getBoundingClientRect() ?? null,
      rightAxisRect: rightAxisEl?.getBoundingClientRect() ?? null,
    };
  }, []);

  const regionAtPoint = useCallback(
    (clientX: number, clientY: number): { region: GestureRegion; plotY: number; plotHeight: number } | null => {
      const measured = measurePlot();
      if (!measured) return null;
      const { plotRect, leftAxisRect, rightAxisRect } = measured;
      const plotHeight = plotRect.height;
      const plotY = clientY - plotRect.top;

      if (leftAxisRect && clientX >= leftAxisRect.left && clientX <= leftAxisRect.right) {
        return { region: 'left', plotY: clientY - plotRect.top, plotHeight };
      }
      if (
        rightAutoRef.current != null &&
        rightAxisRect &&
        clientX >= rightAxisRect.left &&
        clientX <= rightAxisRect.right
      ) {
        return { region: 'right', plotY: clientY - plotRect.top, plotHeight };
      }
      // Expand axis hit targets slightly into the margin so tick labels are easy to grab.
      if (clientX < plotRect.left && clientX >= plotRect.left - 48) {
        return { region: 'left', plotY, plotHeight };
      }
      if (
        rightAutoRef.current != null &&
        clientX > plotRect.right &&
        clientX <= plotRect.right + 36
      ) {
        return { region: 'right', plotY, plotHeight };
      }
      if (
        clientX >= plotRect.left &&
        clientX <= plotRect.right &&
        clientY >= plotRect.top &&
        clientY <= plotRect.bottom
      ) {
        return { region: 'plot', plotY, plotHeight };
      }
      return null;
    },
    [measurePlot]
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button != null && e.button !== 0) return;
      const hit = regionAtPoint(e.clientX, e.clientY);
      if (!hit) return;

      // Plot gestures only once zoomed — otherwise leave the event alone so
      // Ionic sheet scroll and Recharts tooltips keep working.
      if (hit.region === 'plot') {
        const anyZoomed =
          isZoomedView(leftViewRef.current, leftAutoRef.current) ||
          (rightAutoRef.current != null &&
            isZoomedView(rightViewRef.current, rightAutoRef.current));
        if (!anyZoomed) return;
      }

      gestureRef.current = {
        pointerId: e.pointerId,
        region: hit.region,
        startClientX: e.clientX,
        startClientY: e.clientY,
        anchorPlotY: hit.plotY,
        plotHeight: hit.plotHeight,
        startLeft: { ...leftViewRef.current },
        startRight: { ...rightViewRef.current },
        moved: false,
      };

      if (hit.region === 'left' || hit.region === 'right') {
        e.stopPropagation();
        // Prevent the browser double-tap zoom; click-through still fires on tap.
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      } else {
        e.stopPropagation();
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      }
    },
    [regionAtPoint]
  );

  const onPointerMove = useCallback((e: ReactPointerEvent) => {
    const g = gestureRef.current;
    if (!g || g.pointerId !== e.pointerId) return;

    const dx = e.clientX - g.startClientX;
    const dy = e.clientY - g.startClientY;
    if (!g.moved) {
      if (Math.hypot(dx, dy) < POINTER_THRESHOLD_PX) return;
      g.moved = true;
    }

    e.stopPropagation();
    e.preventDefault();

    if (g.region === 'left') {
      setLeftView(
        zoomViewByAxisDrag(
          g.startLeft,
          leftAutoRef.current,
          g.plotHeight,
          g.anchorPlotY,
          dy
        )
      );
      return;
    }
    if (g.region === 'right' && rightAutoRef.current != null) {
      setRightView(
        zoomViewByAxisDrag(
          g.startRight,
          rightAutoRef.current,
          g.plotHeight,
          g.anchorPlotY,
          dy
        )
      );
      return;
    }
    if (g.region === 'plot') {
      // Pan every axis that is currently zoomed — slide [min, max] at constant span.
      if (isZoomedView(g.startLeft, leftAutoRef.current)) {
        setLeftView(
          panViewByPlotDrag(g.startLeft, leftAutoRef.current, g.plotHeight, dy)
        );
      }
      if (rightAutoRef.current != null && isZoomedView(g.startRight, rightAutoRef.current)) {
        setRightView(
          panViewByPlotDrag(g.startRight, rightAutoRef.current, g.plotHeight, dy)
        );
      }
    }
  }, []);

  const onPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      const g = gestureRef.current;
      if (!g || g.pointerId !== e.pointerId) return;
      gestureRef.current = null;

      try {
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      } catch {
        // capture may already be released
      }

      if (g.region !== 'left' && g.region !== 'right') return;

      e.stopPropagation();

      // Double-tap an axis to reset only that axis (suppress browser zoom via
      // preventDefault on the axis pointerdown + touch-action: none).
      if (!g.moved) {
        const now = Date.now();
        const prev = lastTapRef.current;
        if (prev && prev.axis === g.region && now - prev.at <= DOUBLE_TAP_MS) {
          lastTapRef.current = null;
          e.preventDefault();
          resetAxis(g.region);
        } else {
          lastTapRef.current = { axis: g.region, at: now };
        }
      }
    },
    [resetAxis]
  );

  const onPointerCancel = useCallback((e: ReactPointerEvent) => {
    if (gestureRef.current?.pointerId === e.pointerId) {
      gestureRef.current = null;
    }
  }, []);

  const chartAreaProps = {
    ref: chartAreaRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };

  return {
    chartAreaProps,
    chartAreaRef,
    leftView,
    rightView,
    leftDomain: [leftView.min, leftView.max] as [number, number],
    rightDomain: [rightView.min, rightView.max] as [number, number],
    leftTicks,
    rightTicks,
    leftStep,
    rightStep,
    leftZoomed,
    rightZoomed,
    isAnyZoomed,
    resetAll,
    resetAxis,
  };
};

export default useYAxisZoom;
